import { z } from "zod";

const optional = () => z.string().optional().or(z.literal(""));

export const planInputSchema = z.object({
  inventory_batch_id: z.string().min(1, "Batch required"),
  planned_qty: z.coerce.number().min(0),
  planned_weight: z.coerce.number().min(0),
});

export const planOutputSchema = z.object({
  material_id: z.string().min(1, "Material required"),
  material_grade_id: optional(),
  shape_size_id: optional(),
  planned_qty: z.coerce.number().min(0),
  planned_weight: z.coerce.number().min(0),
  thickness_mm: z.union([z.coerce.number(), z.nan(), z.literal("")]).optional(),
  width_mm: z.union([z.coerce.number(), z.nan(), z.literal("")]).optional(),
  length_mm: z.union([z.coerce.number(), z.nan(), z.literal("")]).optional(),
  notes: optional(),
});

export const planStageSchema = z.object({
  process_type_id: z.string().min(1, "Process required"),
  machine_id: z.string().min(1, "Machine required"),
  input_source: z.enum(["inventory", "previous_stage"]),
  planned_start_at: optional(),
  planned_end_at: optional(),
  quality_check_required: z.boolean(),
  notes: optional(),
  inputs: z.array(planInputSchema),
  outputs: z.array(planOutputSchema).min(1, "Add at least one output"),
});

export const processPlanFormSchema = z
  .object({
    plan_date: z.string().min(1, "Date required"),
    sales_order_id: optional(),
    sales_order_line_id: optional(),
    notes: optional(),
    stages: z.array(planStageSchema).min(1, "Add at least one stage"),
  })
  .superRefine((v, ctx) => {
    v.stages.forEach((stage, i) => {
      if (stage.input_source === "previous_stage" && i === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "First stage cannot use previous stage",
          path: ["stages", i, "input_source"],
        });
      }
      if (stage.input_source === "inventory" && stage.inputs.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Add an inventory input",
          path: ["stages", i, "inputs"],
        });
      }
    });
  });

export type ProcessPlanFormValues = z.infer<typeof processPlanFormSchema>;

export const emptyPlanInput = {
  inventory_batch_id: "",
  planned_qty: 0,
  planned_weight: 0,
};

export const emptyPlanOutput = {
  material_id: "",
  material_grade_id: "",
  shape_size_id: "",
  planned_qty: 0,
  planned_weight: 0,
  thickness_mm: undefined as number | undefined,
  width_mm: undefined as number | undefined,
  length_mm: undefined as number | undefined,
  notes: "",
};

export const emptyPlanStage = {
  process_type_id: "",
  machine_id: "",
  input_source: "inventory" as const,
  planned_start_at: "",
  planned_end_at: "",
  quality_check_required: true,
  notes: "",
  inputs: [{ ...emptyPlanInput }],
  outputs: [{ ...emptyPlanOutput }],
};

export const emptyProcessPlanForm: ProcessPlanFormValues = {
  plan_date: new Date().toISOString().slice(0, 10),
  sales_order_id: "",
  sales_order_line_id: "",
  notes: "",
  stages: [{ ...emptyPlanStage, inputs: [{ ...emptyPlanInput }], outputs: [{ ...emptyPlanOutput }] }],
};
